import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { createApi } from '../lib/api';
import { blogPosts as staticPosts } from '../data/blogPosts';
import { Search, Loader2, ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import BlogCard from '../components/blog/BlogCard';
import BlogSidebarAds from '../components/blog/BlogSidebarAds';
import BlogBlocksRenderer from '../components/blog/BlogBlocksRenderer';
import BlogShareBar from '../components/blog/BlogShareBar';
import BlogReactions from '../components/blog/BlogReactions';

// --- Helpers ---

// Normalize static data to match API schema roughly
function normalizeStatic(post) {
  return {
    id: post.slug, // Fake ID
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    author_name: post.author,
    published_at: new Date(post.date).getTime() / 1000,
    thumbnail_url: null, // Static doesn't have thumbs
    content_html: post.content.replace(/\n/g, '<br/>'), // Simple conversion
    blocks_json: JSON.stringify(
      post.content.split(/\n\s*\n/).map((para, i) => ({
        type: 'paragraph',
        content: para.trim(),
        id: `static-${i}`
      })).filter(b => b.content)
    ),
    type: 'article',
    reading_time: 3, // Default estimate
    is_featured: false
  };
}

// Estimate reading time from content
function calculateReadingTime(text) {
  const wordsPerMinute = 200;
  const count = text ? text.split(/\s+/).length : 0;
  return Math.ceil(count / wordsPerMinute);
}

// --- List Page ---

export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const POSTS_PER_PAGE = 6;

  useEffect(() => {
    async function fetchPosts() {
      setLoading(true);
      try {
        const api = createApi();
        // Fetch more initially to separate featured
        const res = await api.get('/blog/posts?limit=50'); 
        
        let fetchedPosts = [];
        if (res.ok && Array.isArray(res.posts) && res.posts.length > 0) {
           fetchedPosts = res.posts;
        } else {
           // Fallback to static
           console.warn("API/DB empty or failed, falling back to static blog posts");
           fetchedPosts = staticPosts.map(normalizeStatic);
        }
        
        // Sort by date desc
        fetchedPosts.sort((a, b) => b.published_at - a.published_at);
        setPosts(fetchedPosts);
      } catch (err) {
        console.error("Failed to fetch posts", err);
        setPosts(staticPosts.map(normalizeStatic));
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, []);

  // Filter & Pagination Logic
  const filteredPosts = useMemo(() => {
    if (!searchQuery) return posts;
    const q = searchQuery.toLowerCase();
    return posts.filter(p => 
      p.title.toLowerCase().includes(q) || 
      (p.excerpt && p.excerpt.toLowerCase().includes(q))
    );
  }, [posts, searchQuery]);

  // Featured Post Logic (Top 1 featured or just newest)
  const featuredPost = useMemo(() => {
    if (page > 1 || searchQuery) return null; // Only show on first page w/o search
    return filteredPosts.find(p => p.is_featured) || filteredPosts[0];
  }, [filteredPosts, page, searchQuery]);

  // Remaining Posts for Grid
  const gridPosts = useMemo(() => {
    let list = filteredPosts;
    if (featuredPost && !searchQuery && page === 1) {
      list = list.filter(p => p.id !== featuredPost.id);
    }
    // Pagination
    const start = (page - 1) * POSTS_PER_PAGE;
    return list.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, featuredPost, page, searchQuery]);

  const totalPages = Math.ceil(
    (searchQuery ? filteredPosts.length : filteredPosts.length - (featuredPost && page === 1 ? 1 : 0)) / POSTS_PER_PAGE
  );

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-[#0b0b12]">
      <Helmet>
        <title>Blog | AI Hairstyle Studio</title>
        <meta name="description" content="Discover the latest trends, tips, and guides on AI hairstyles and virtual makeovers." />
        <link rel="canonical" href="https://aihairstylestudio.com/blog" />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Header & Search */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
              The <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 to-purple-600">Style Edit</span>
            </h1>
            <p className="text-white/60 text-lg max-w-xl">
              Expert tips, hairstyle trends, and product updates from the AI Hairstyle Studio team.
            </p>
          </div>
          
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
            <input 
              type="text" 
              placeholder="Search articles..." 
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-fuchsia-500/50 outline-none transition-colors"
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-fuchsia-500 w-10 h-10" />
          </div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Main Content Area */}
            <div className="lg:col-span-8 space-y-12">
              
              {featuredPost && (
                <div className="mb-12">
                   <Link to={`/blog/${featuredPost.slug}`} className="group block relative aspect-[16/9] md:aspect-[2/1] rounded-2xl overflow-hidden mb-6 bg-white/5 border border-white/5 shadow-2xl">
                      {featuredPost.thumbnail_url ? (
                        <img 
                          src={featuredPost.thumbnail_url} 
                          alt={featuredPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-fuchsia-900/40 to-purple-900/40">
                          <span className="text-white/20 text-4xl font-bold">AI Hairstyle Studio</span>
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent flex flex-col justify-end p-6 md:p-10">
                        <div className="flex items-center gap-3 mb-3">
                          {featuredPost.is_featured && (
                            <span className="inline-block px-3 py-1 bg-fuchsia-600 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-lg shadow-fuchsia-900/20">
                              Featured
                            </span>
                          )}
                          <span className="inline-block px-3 py-1 bg-black/40 backdrop-blur border border-white/10 text-white/80 text-xs font-bold uppercase tracking-wider rounded-full">
                            {(featuredPost.reading_time_minutes || 5)} min read
                          </span>
                        </div>

                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-3 group-hover:text-fuchsia-400 transition-colors leading-tight drop-shadow-lg">
                          {featuredPost.title}
                        </h2>
                        
                        <p className="text-lg text-white/80 mb-4 line-clamp-2 max-w-3xl drop-shadow-md hidden md:block">
                          {featuredPost.excerpt}
                        </p>

                        <div className="flex items-center gap-4 text-white/70 text-sm font-medium">
                          <span>{featuredPost.author_name || 'AI Hairstyle Studio'}</span>
                          <span>•</span>
                          <span>
                            {featuredPost.published_at 
                              ? new Date(Number(featuredPost.published_at) * 1000).toLocaleDateString() 
                              : new Date().toLocaleDateString()
                            }
                          </span>
                        </div>
                      </div>
                   </Link>
                </div>
              )}

              {/* Grid Layout */}
              {gridPosts.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {gridPosts.map(post => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              ) : (
                !featuredPost && (
                  <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/5">
                    <p className="text-white/40 text-lg">No articles found matching your search.</p>
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="mt-4 text-fuchsia-400 hover:text-fuchsia-300 font-medium"
                    >
                      Clear search
                    </button>
                  </div>
                )
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 pt-8">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-3 rounded-xl border border-white/10 text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <span className="flex items-center px-4 font-mono text-white/60">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-3 rounded-xl border border-white/10 text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                  >
                    <ArrowRight size={20} />
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar (Desktop Only) */}
            <div className="hidden lg:block lg:col-span-4 pl-8 border-l border-white/5 space-y-8">
               
               {/* Popular Posts Widget */}
               {posts.filter(p => !featuredPost || p.id !== featuredPost.id).length > 0 && (
                 <div className="bg-[#15151e] border border-white/5 rounded-2xl p-6">
                   <h3 className="text-lg font-bold text-white mb-4 border-b border-white/5 pb-2">Top Stories</h3>
                   <div className="space-y-4">
                     {posts
                       .filter(p => !featuredPost || p.id !== featuredPost.id)
                       .sort((a, b) => (b.likes || 0) - (a.likes || 0))
                       .slice(0, 5)
                       .map((post, idx) => (
                         <Link key={post.id} to={`/blog/${post.slug}`} className="group flex gap-4 items-start">
                           <span className="text-2xl font-bold text-white/10 group-hover:text-fuchsia-500/20 transition-colors font-mono">
                             {String(idx + 1).padStart(2, '0')}
                           </span>
                           <div>
                             <h4 className="text-sm font-semibold text-white/90 group-hover:text-fuchsia-400 transition-colors line-clamp-2 leading-snug mb-1">
                               {post.title}
                             </h4>
                             <span className="text-xs text-white/40 block">
                               {post.published_at 
                                 ? new Date(Number(post.published_at) * 1000).toLocaleDateString() 
                                 : 'Recently'
                               } • {post.reading_time_minutes || 5} min read
                             </span>
                           </div>
                         </Link>
                       ))}
                   </div>
                 </div>
               )}

               <BlogSidebarAds />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Detail Page ---

export function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPost() {
      setLoading(true);
      setError(null);
      try {
        const api = createApi();
        const res = await api.get(`/blog/posts/${slug}`);
        
        if (res.ok && res.post) {
           setPost(res.post);
        } else {
           // Fallback to static if API returns 404 or empty
           const staticPost = staticPosts.find(p => p.slug === slug);
           if (staticPost) {
              setPost(normalizeStatic(staticPost));
           } else {
              setError("Post not found");
           }
        }
      } catch (err) {
        console.error("Failed to fetch post", err);
        // Fallback on error
        const staticPost = staticPosts.find(p => p.slug === slug);
        if (staticPost) {
           setPost(normalizeStatic(staticPost));
        } else {
           setError("Post not found");
        }
      } finally {
        setLoading(false);
      }
    }
    
    if (slug) fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#0b0b12]">
        <Loader2 className="animate-spin text-fuchsia-500 w-12 h-12" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4 bg-[#0b0b12]">
        <h2 className="text-2xl font-bold text-white mb-2">Article Not Found</h2>
        <p className="text-white/60 mb-6">The article you are looking for doesn't exist or has been removed.</p>
        <Link to="/blog" className="px-6 py-2 bg-fuchsia-600 rounded-lg text-white font-medium hover:bg-fuchsia-500 transition-colors">
          Back to Blog
        </Link>
      </div>
    );
  }

  // Parse Blocks
  let contentBlocks = [];
  if (post.blocks_json) {
    try {
      contentBlocks = typeof post.blocks_json === 'string' 
        ? JSON.parse(post.blocks_json) 
        : post.blocks_json;
    } catch (e) {
      console.error("Failed to parse blocks", e);
    }
  }

  // Construct JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "image": post.thumbnail_url ? [post.thumbnail_url] : [],
    "datePublished": post.published_at ? new Date(post.published_at * 1000).toISOString() : new Date().toISOString(),
    "dateModified": post.updated_at ? new Date(post.updated_at * 1000).toISOString() : new Date().toISOString(),
    "author": [{
      "@type": "Person",
      "name": post.author_name || "AI Hairstyle Studio"
    }],
    "description": post.excerpt || ""
  };

  // SEO & Social Meta Helpers
  const SITE_URL = "https://aihairstylestudio.com";
  const pageUrl = `${SITE_URL}/blog/${post.slug}`;
  const canonical = post.canonical_url || pageUrl;
  const title = post.seo_title || post.title;
  const description = post.meta_description || post.excerpt || `Read ${post.title} on AI Hairstyle Studio.`;
  
  let imageUrl = post.thumbnail_url;
  if (!imageUrl) imageUrl = `${SITE_URL}/social-preview.jpg`; // Fallback
  else if (imageUrl.startsWith('/')) imageUrl = `${SITE_URL}${imageUrl}`;


  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-[#0b0b12]">
      <Helmet>
        <title>{title} | AI Hairstyle Studio</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonical} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />

        <script type="application/ld+json">
          {post.schema_json || JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <Link to="/blog" className="inline-flex items-center gap-2 text-white/50 hover:text-white mb-8 transition-colors group">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to Blog
        </Link>
        
        {/* Article Header */}
        <header className="mb-12 text-center md:text-left max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/60 mb-4 justify-center md:justify-start">
            <span className="px-3 py-1 bg-white/5 rounded-full border border-white/5 uppercase text-xs font-bold tracking-wider">
              {post.type === 'video' ? 'Video' : 'Article'}
            </span>
            <span className="flex items-center gap-1.5">
               <Clock size={14} />
               <span>{(post.reading_time_minutes || post.reading_time || 5)} min read</span>
            </span>
            <span className="text-white/20">|</span>
            <span>
               {post.published_at ? new Date(post.published_at * 1000).toLocaleDateString() : 'Recently'}
            </span>
            {post.author_name && (
              <>
                 <span className="text-white/20">|</span>
                 <span>By {post.author_name}</span>
              </>
            )}
          </div>
          
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {post.title}
          </h1>
          
          {post.excerpt && (
            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              {post.excerpt}
            </p>
          )}

          {post.thumbnail_url && (
             <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 aspect-video mb-12 bg-white/5">
                <img 
                  src={post.thumbnail_url} 
                  alt={post.title} 
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
             </div>
          )}
        </header>

        {/* Article Body & Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
           {/* Main Content */}
           <div className="lg:col-span-8 bg-[#0b0b12] overflow-hidden">
             {/* Content Renderer */}
             {contentBlocks && contentBlocks.length > 0 ? (
                <div className="mb-16">
                  <BlogBlocksRenderer blocks={contentBlocks} />
                </div>
             ) : (
                <div className="prose prose-invert prose-lg max-w-none prose-headings:text-white prose-p:text-white/80 prose-a:text-fuchsia-400 prose-strong:text-white mb-16">
                   {post.content_html ? (
                     <div dangerouslySetInnerHTML={{ __html: post.content_html }} />
                   ) : (
                     <div dangerouslySetInnerHTML={{ __html: post.content_html || "" }} />
                   )}
                </div>
             )}

             <hr className="border-white/10 mb-12" />

             <div className="bg-white/5 border border-white/5 rounded-2xl p-6 md:p-8 mb-12">
                <h3 className="text-lg font-bold text-white mb-4">Share this article</h3>
                <BlogShareBar 
                  url={canonical} 
                  title={title} 
                  description={description} 
                  image={imageUrl} 
                />
             </div>
             
             <BlogReactions 
               slug={post.slug}
               likes={post.likes || 0}
               dislikes={post.dislikes || 0}
             />
           </div>

           {/* Sidebar */}
           <div className="lg:col-span-4 space-y-8 mt-8 lg:mt-0">
              <aside className="lg:sticky lg:top-24 space-y-8">
                {/* Slot 1 */}
                {post.sidebar_html_1 ? (
                   <div className="rounded-xl overflow-hidden" dangerouslySetInnerHTML={{ __html: post.sidebar_html_1 }} />
                ) : (
                   <div>
                      <BlogSidebarAds slot="top" />
                   </div>
                )}

                {/* Slot 2 */}
                {post.sidebar_html_2 ? (
                   <div dangerouslySetInnerHTML={{ __html: post.sidebar_html_2 }} />
                ) : (
                   <div className="p-6 bg-[#1f1f2e] rounded-xl border border-white/5">
                      <h4 className="text-white font-bold mb-2">Popular Styles</h4>
                      <p className="text-sm text-white/60 mb-4">Check out our generated hairstyles gallery.</p>
                      <Link to="/styles" className="block w-full py-2 bg-fuchsia-600 rounded text-center text-sm font-medium text-white hover:bg-fuchsia-500">
                        View Gallery
                      </Link>
                   </div>
                )}

                {/* Slot 3 */}
                {post.sidebar_html_3 && (
                   <div dangerouslySetInnerHTML={{ __html: post.sidebar_html_3 }} />
                )}
                
                {/* Slot 4 */}
                {post.sidebar_html_4 && (
                   <div dangerouslySetInnerHTML={{ __html: post.sidebar_html_4 }} />
                )}
              </aside>
           </div>
        </div>
      </div>
    </div>
  );
}
